import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  List,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DownloadOutlined,
  FileOutlined,
  ExportOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Settings from "../../dependencies/custom/settings";
import Utilities from "../../dependencies/custom/react-utilities";
import { createLmsApi, getLmsErrorMessage } from "../../lms/api";
import { LmsPageHeader } from "../../lms/components/LmsPrimitives";
import { LMS_CONTENT_TYPES } from "../../lms/constants";
import LearnerQuizzes from "./LearnerQuizzes";
import LearnerLiveSessions from "./LearnerLiveSessions";

const { Text, Title } = Typography;

function fileLabel(file) {
  return file?.title || file?.original_name || "Learning file";
}

function contentTypeLabel(value) {
  return LMS_CONTENT_TYPES.find((type) => type.value === value)?.label || value;
}

function isMediaFile(file) {
  const name = String(file?.original_name || file?.title || "").toLowerCase();
  return /\.(mp4|mov|webm|avi|mkv|mp3|wav|ogg|aac|flac)$/i.test(name);
}

function isVideoFile(file) {
  const name = String(file?.original_name || file?.title || "").toLowerCase();
  return /\.(mp4|mov|webm|avi|mkv)$/i.test(name);
}

function formatSize(size) {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function LmsLearning() {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useMemo(() => createLmsApi(navigate), [navigate]);
  const [applications, setApplications] = useState([]);
  const [selectedStudentID, setSelectedStudentID] = useState();
  const [learner, setLearner] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [pastGroups, setPastGroups] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState();
  const [scopeFilter, setScopeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState();
  const [classFilter, setClassFilter] = useState();
  const [streamFilter, setStreamFilter] = useState();
  const [selectedContentID, setSelectedContentID] = useState();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [loadingFileID, setLoadingFileID] = useState(null);
  const [error, setError] = useState("");
  const [applicationsReady, setApplicationsReady] = useState(false);
  const contentRequestID = useRef(0);
  const mediaRequestID = useRef(0);

  const loadContent = useCallback(async (studentID) => {
    const requestID = ++contentRequestID.current;
    ++mediaRequestID.current;
    setMedia(null);
    setMediaLoading(false);
    setLoadingFileID(null);
    setCurrentGroup(null);
    setPastGroups([]);
    setLearner(null);
    setSelectedContentID(undefined);
    setLoading(true);
    setError("");
    const response = await api.learnerContent(studentID ? { studentID } : undefined);
    if (requestID !== contentRequestID.current) return;
    if (!response?.ok) {
      setCurrentGroup(null);
      setPastGroups([]);
      setError(getLmsErrorMessage(response, "Unable to load learning content"));
    } else {
      const data = response.data || {};
      const current = data.current && typeof data.current === "object"
        ? data.current
        : { contents: Array.isArray(data.contents) ? data.contents : [] };
      const past = Array.isArray(data.past) ? data.past : [];
      const items = [
        ...(Array.isArray(current.contents) ? current.contents : []),
        ...past.flatMap((group) => Array.isArray(group.contents) ? group.contents : []),
      ];
      setCurrentGroup(current);
      setPastGroups(past);
      setLearner(data);
      setSelectedContentID((current) =>
        items.some((item) => String(item.id) === String(current))
          ? current
          : items[0]?.id,
      );
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    let active = true;
    async function loadLearners() {
      const response = await Utilities.fetchWithAuth(
        "GET",
        Settings.urls.enrollment?.related_applications,
        undefined,
        navigate,
      );
      if (!active) return;
      if (response?.ok) {
        const records = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : [];
        const admitted = records.filter(
          (application) => application?.status?.toLowerCase() === "admitted" && application?.student?.id,
        );
        setApplications(admitted);
      } else {
        setApplications([]);
      }
      setApplicationsReady(true);
    }
    loadLearners();
    return () => { active = false; };
  }, [navigate]);

  useEffect(() => {
    if (!applicationsReady) return;
    const requestedID = new URLSearchParams(location.search).get("studentID");
    const admittedMatch = requestedID && applications.find(
      (application) => String(application.student.id) === String(requestedID),
    );
    const studentID = admittedMatch?.student.id || applications[0]?.student?.id;
    setSelectedStudentID(studentID);
    if (requestedID && String(requestedID) !== String(studentID || "")) {
      const params = new URLSearchParams(location.search);
      if (studentID) params.set("studentID", studentID);
      else params.delete("studentID");
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
      return;
    }
    loadContent(studentID);
  }, [applicationsReady, applications, location.pathname, location.search, loadContent, navigate]);

  useEffect(() => {
    let active = true;
    api.sessions.list({ status: "Active", pageSize: 100 }).then((response) => {
      if (!active || !response?.ok) return;
      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.results)
          ? response.data.results
          : [];
      setSessions(items.filter((session) => session.status === "Active"));
    });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (!media?.url) return undefined;
    return () => {
      if (media.url.startsWith("blob:")) URL.revokeObjectURL(media.url);
    };
  }, [media]);

  const currentContents = Array.isArray(currentGroup?.contents) ? currentGroup.contents : [];
  const allContents = [
    ...currentContents,
    ...pastGroups.flatMap((group) => Array.isArray(group.contents) ? group.contents : []),
  ];
  const selectedContent = allContents.find(
    (item) => String(item.id) === String(selectedContentID),
  );
  const types = [...new Set(allContents.map((item) => item.content_type).filter(Boolean))];
  const groups = [
    ...(currentGroup ? [{ ...currentGroup, scope: "current", key: "current" }] : []),
    ...pastGroups.map((group, index) => ({ ...group, scope: "past", key: `past-${index}` })),
  ];
  const years = [...new Set(groups.map((group) => group.academicYear).filter((year) => year != null))]
    .sort((a, b) => String(b).localeCompare(String(a), undefined, { numeric: true }));
  const classes = [...new Map(groups.filter((group) => group.classID != null).map((group) => [
    String(group.classID), { value: group.classID, label: group.className || `Class ${group.classID}` },
  ])).values()];
  const streams = [...new Map(groups.filter((group) => group.streamID != null).map((group) => [
    String(group.streamID), { value: group.streamID, label: group.streamName || `Stream ${group.streamID}` },
  ])).values()];
  const filteredGroups = groups
    .filter((group) => scopeFilter === "all" || group.scope === scopeFilter)
    .filter((group) => !yearFilter || String(group.academicYear) === String(yearFilter))
    .filter((group) => !classFilter || String(group.classID) === String(classFilter))
    .filter((group) => !streamFilter || String(group.streamID) === String(streamFilter))
    .sort((a, b) => {
      if (a.scope !== b.scope) return a.scope === "current" ? -1 : 1;
      return String(b.academicYear || "").localeCompare(String(a.academicYear || ""), undefined, { numeric: true });
    });
  const filterContent = (item, group) => {
    const searchText = `${item.title || ""} ${item.description || ""} ${item.sessionTitle || ""} ${group.className || ""} ${group.streamName || ""} ${group.academicYear || ""}`.toLowerCase();
    return (!query || searchText.includes(query.toLowerCase())) && (!typeFilter || item.content_type === typeFilter);
  };
  const hasFilteredContent = filteredGroups.some((group) =>
    (Array.isArray(group.contents) ? group.contents : []).some((item) => filterContent(item, group)),
  );

  const renderContentItem = (content) => (
    <List.Item
      key={content.id}
      onClick={() => handleContentChange(content)}
      style={{
        cursor: "pointer",
        padding: 12,
        borderRadius: 6,
        background: String(content.id) === String(selectedContentID) ? "#e6f4ff" : undefined,
      }}
    >
      <List.Item.Meta
        title={content.title}
        description={
          <Space direction="vertical" size={2}>
            <Text type="secondary">{content.sessionTitle || "Learning session"}</Text>
            <Space size={4} wrap>
              {content.content_type && <Tag>{contentTypeLabel(content.content_type)}</Tag>}
              {content.week_number && <Tag color="blue">Week {content.week_number}</Tag>}
            </Space>
          </Space>
        }
      />
    </List.Item>
  );

  const recordActivity = async (content, activityType) => {
    const matches = sessions.filter(
      (session) => session.title === content.sessionTitle,
    );
    if (matches.length !== 1) return false;
    const response = await api.recordLearnerActivitySelf({
      ...(learner?.studentID ? { studentID: learner.studentID } : {}),
      lmsSessionID: matches[0].id,
      contentID: content.id,
      activityType,
    });
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to record learning activity"));
      return false;
    }
    return true;
  };

  const openFile = async (content, file) => {
    const requestID = ++mediaRequestID.current;
    setMedia(null);
    setLoadingFileID(file?.id);
    if (file?.id && isMediaFile(file)) {
      setMediaLoading(true);
      const response = await api.request("GET", "learnerContentStream", {
        pathParams: { fileId: file.id },
        query: {
          ...(learner?.studentID ? { studentID: learner.studentID } : {}),
          play: true,
        },
      });
      if (requestID !== mediaRequestID.current) return;
      setMediaLoading(false);
      setLoadingFileID(null);
      const mediaUrl = response?.data?.streamUrl || file.streamUrl || file.fileUrl;
      if (!response?.ok || !mediaUrl) {
        message.error(getLmsErrorMessage(response, "Unable to open this media file"));
        return;
      }
      setMedia({ url: mediaUrl, file, video: isVideoFile(file) });
      await recordActivity(content, "content_view");
      return;
    }

    if (file?.id) {
      const response = await api.request("GET", "learnerContentStream", {
        pathParams: { fileId: file.id },
        query: learner?.studentID ? { studentID: learner.studentID } : undefined,
      });
      if (requestID !== mediaRequestID.current) return;
      setLoadingFileID(null);
      if (!response?.ok) {
        message.error(getLmsErrorMessage(response, "Unable to open this file"));
        return;
      }
      const downloadUrl = response.data?.downloadUrl;
      if (!downloadUrl) {
        message.error("The file is not available for download");
        return;
      }
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
      await recordActivity(content, "content_view");
      return;
    }

    if (file?.fileUrl) {
      window.open(file.fileUrl, "_blank", "noopener,noreferrer");
      setLoadingFileID(null);
      await recordActivity(content, "content_view");
    } else {
      setLoadingFileID(null);
      message.error("This file has no available link");
    }
  };

  const downloadFile = async (content, file) => {
    if (!file?.id) return openFile(content, file);
    setLoadingFileID(file.id);
    if (isMediaFile(file)) {
      const response = await api.request("GET", "learnerContentStream", {
        pathParams: { fileId: file.id },
        query: learner?.studentID ? { studentID: learner.studentID } : undefined,
        requestOptions: { responseType: "blob" },
      });
      setLoadingFileID(null);
      if (!response?.ok) {
        message.error(getLmsErrorMessage(response, "Unable to download this media file"));
        return;
      }
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.original_name || fileLabel(file);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      await recordActivity(content, "file_download");
      return;
    }
    const response = await api.request("GET", "learnerContentStream", {
      pathParams: { fileId: file.id },
      query: learner?.studentID ? { studentID: learner.studentID } : undefined,
    });
    setLoadingFileID(null);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to download this file"));
      return;
    }
    const url = response.data?.downloadUrl;
    if (!url) {
      message.error("The file is not available for download");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    await recordActivity(content, "file_download");
  };

  const handleContentChange = async (content) => {
    ++mediaRequestID.current;
    setSelectedContentID(content.id);
    setMedia(null);
    setMediaLoading(false);
    setLoadingFileID(null);
    await recordActivity(content, "content_view");
  };

  const handleStudentChange = (studentID) => {
    ++mediaRequestID.current;
    setSelectedStudentID(studentID);
    setCurrentGroup(null);
    setPastGroups([]);
    setLearner(null);
    setMedia(null);
    setMediaLoading(false);
    setLoadingFileID(null);
    setSelectedContentID(undefined);
    setYearFilter(undefined);
    setClassFilter(undefined);
    setStreamFilter(undefined);
    const params = new URLSearchParams(location.search);
    params.set("studentID", studentID);
    navigate({ pathname: location.pathname, search: `?${params}` });
  };

  const requestedLiveStudentID = new URLSearchParams(location.search).get("studentID");
  const liveStudentID = applications.find(
    (application) => String(application.student.id) === requestedLiveStudentID,
  )?.student.id || applications[0]?.student?.id;

  const openLearnerInNewTab = () => {
    if (!selectedStudentID) return;
    const params = new URLSearchParams(location.search);
    params.set("studentID", selectedStudentID);
    const url = new URL(location.pathname, window.location.origin);
    url.search = params.toString();
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ padding: 24 }}>
      <LmsPageHeader
        title="Learning materials"
        description="Browse learning resources for your current and past classes."
      />

      {applications.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            {applications.length > 1 && <>
              <Text strong>Learning as</Text>
              <Select
                style={{ minWidth: 240 }}
                value={selectedStudentID}
                onChange={handleStudentChange}
                options={applications.map((application) => ({
                  value: application.student.id,
                  label: application.student_name || `${application.student.firstName || ""} ${application.student.lastName || ""}`.trim(),
                }))}
              />
            </>}
            <Button icon={<ExportOutlined />} onClick={openLearnerInNewTab} disabled={!selectedStudentID}>
              Open learner in new tab
            </Button>
          </Space>
        </Card>
      )}

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {!loading && learner && (
        <Alert
          type="info"
          showIcon
          message={learner.student || "Your learning materials"}
          description={learner.studentClass ? `Class: ${learner.studentClass}` : undefined}
          style={{ marginBottom: 16 }}
        />
      )}

      {applicationsReady && (
        <LearnerLiveSessions
          key={liveStudentID || "default"}
          api={api}
          studentID={liveStudentID}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card
            title="Available content"
            extra={<Button type="text" icon={<ReloadOutlined />} onClick={() => loadContent(selectedStudentID)} aria-label="Refresh content" />}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Input.Search
                placeholder="Search learning materials"
                allowClear
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <Select
                allowClear
                placeholder="All content types"
                style={{ width: "100%" }}
                value={typeFilter}
                onChange={setTypeFilter}
                options={types.map((type) => ({ value: type, label: contentTypeLabel(type) }))}
              />
              <Select
                value={scopeFilter}
                style={{ width: "100%" }}
                onChange={setScopeFilter}
                options={[
                  { value: "all", label: "Current and past" },
                  { value: "current", label: "Current class" },
                  { value: "past", label: "Past classes" },
                ]}
              />
              <Select allowClear placeholder="All academic years" style={{ width: "100%" }} value={yearFilter} onChange={setYearFilter} options={years.map((year) => ({ value: year, label: String(year) }))} />
              <Select allowClear placeholder="All classes" style={{ width: "100%" }} value={classFilter} onChange={setClassFilter} options={classes} />
              <Select allowClear placeholder="All streams" style={{ width: "100%" }} value={streamFilter} onChange={setStreamFilter} options={streams} />
              {loading ? <div style={{ textAlign: "center", padding: 32 }}><Spin /></div> : hasFilteredContent ? (
                filteredGroups.map((group) => {
                  const items = (Array.isArray(group.contents) ? group.contents : []).filter((item) => filterContent(item, group));
                  if (!items.length) return null;
                  const sectionTitle = group.scope === "current" ? "Current class" : "Past class";
                  const groupDescription = [group.academicYear, group.className, group.streamName].filter(Boolean).join(" · ");
                  return (
                    <div key={group.key} style={{ marginTop: 16 }}>
                      <Text strong>{sectionTitle}</Text>
                      {groupDescription && <div><Text type="secondary">{groupDescription}</Text></div>}
                      <List dataSource={items} renderItem={renderContentItem} />
                    </div>
                  );
                })
              ) : (
                <Empty description={allContents.length ? "No content matches these filters" : "No learning content is available yet"} />
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card>
            {!selectedContent ? <Empty description="Choose a learning item to see its details" /> : (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div>
                  <Title level={4} style={{ marginBottom: 4 }}>{selectedContent.title}</Title>
                  <Text type="secondary">{selectedContent.sessionTitle || "Learning session"}</Text>
                  {selectedContent.content_type && <Tag style={{ marginLeft: 8 }}>{contentTypeLabel(selectedContent.content_type)}</Tag>}
                </div>
                {selectedContent.description && <Text>{selectedContent.description}</Text>}

                {mediaLoading && <div style={{ textAlign: "center", padding: 24 }}><Spin tip="Loading media…" /></div>}
                {media && (
                  media.video ? (
                    <video key={media.url} controls preload="metadata" playsInline style={{ width: "100%", maxHeight: 480, background: "#000" }} src={media.url}>
                      Your browser does not support video playback.
                    </video>
                  ) : (
                    <audio key={media.url} controls preload="metadata" style={{ width: "100%" }} src={media.url}>
                      Your browser does not support audio playback.
                    </audio>
                  )
                )}

                <List
                  header={<Text strong>Files and resources</Text>}
                  bordered
                  dataSource={selectedContent.contentFiles || []}
                  locale={{ emptyText: "No files attached to this learning item" }}
                  renderItem={(file) => (
                    <List.Item
                      actions={[
                        <Button
                          key="open"
                          type="link"
                          icon={isMediaFile(file) ? <PlayCircleOutlined /> : <FileOutlined />}
                          loading={mediaLoading && String(loadingFileID) === String(file.id)}
                          onClick={() => openFile(selectedContent, file)}
                        >
                          {isMediaFile(file) ? "Watch / listen" : "Open"}
                        </Button>,
                        <Button
                          key="download"
                          type="link"
                          icon={<DownloadOutlined />}
                          loading={String(loadingFileID) === String(file.id) && !mediaLoading}
                          onClick={() => downloadFile(selectedContent, file)}
                        >
                          Download
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        title={fileLabel(file)}
                        description={formatSize(file.file_size) || file.original_name}
                      />
                    </List.Item>
                  )}
                />
                <LearnerQuizzes
                  key={`${learner?.studentID || "learner"}-${selectedContent.id}`}
                  api={api}
                  contentID={selectedContent.id}
                  studentID={learner?.studentID}
                  onAttempt={() => recordActivity(selectedContent, "quiz_attempt")}
                />
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
